import { postToDevice, type PrintStatus } from './deviceBridge'

/**
 * Printing through Tablet Browser (2.22, task 2449; docs/tablets.md, "Printing"): the one adapter
 * between this site and the app's two print commands. Every name the app reads is here, so a
 * rename on the app's side is a one-line change.
 *
 * - `print-page`: a whole HTML page through Android's print dialog. The driver taps Print;
 *   Android remembers the last printer. The page is printed with JavaScript OFF, so its barcodes
 *   are inline SVG already, and nothing is fetched: it prints with the internet down.
 * - `print-raw`: bytes (ZPL) straight to a Zebra on port 9100. Not used by a screen here yet;
 *   kept beside its twin so both follow the one contract.
 *
 * A request and ONE answer, matched by its requestId, as for the camera scanner: the screen that
 * asked is the one answered. An app older than 2.22 is never asked (`appPrints` in tabletInfo.ts);
 * a request still unanswered after its limit counts as an error, so a screen never waits forever.
 */

export const PRINT_COMMANDS = { raw: 'print-raw', page: 'print-page' } as const

export type PrintAnswer = {
  status: PrintStatus
  /** The app's stable reason (`busy`, `connect-timeout`, ...), for choosing words. */
  errorCode: string
  /** Plain English from the app, shown as it is. */
  errorMessage: string
}

/** A print dialog may stay open fifteen minutes before the app answers it itself. */
export const PAGE_LIMIT_MS = 16 * 60_000
/** The app takes 5 s to connect and 20 to send. */
export const RAW_LIMIT_MS = 30_000

const waiting = new Map<string, (answer: PrintAnswer) => void>()
let asked = 0

function request(message: Record<string, string>, limitMs: number): Promise<PrintAnswer> {
  asked += 1
  const requestId = `print-${Date.now().toString(36)}-${asked}`
  return new Promise((resolve) => {
    const timer = setTimeout(() => finish({ status: 'error', errorCode: 'no-answer', errorMessage: 'The tablet app did not answer.' }), limitMs)
    function finish(answer: PrintAnswer) {
      clearTimeout(timer)
      waiting.delete(requestId)
      resolve(answer)
    }
    waiting.set(requestId, finish)
    if (!postToDevice({ ...message, requestId })) finish({ status: 'error', errorCode: 'no-app', errorMessage: 'There is no tablet app to print through.' })
  })
}

/** A whole page through Android's print dialog. */
export function requestPrintPage(page: { title: string, size: 'letter' | '4x6', html: string }, limitMs = PAGE_LIMIT_MS): Promise<PrintAnswer> {
  return request({ command: PRINT_COMMANDS.page, title: page.title, size: page.size, html: page.html }, limitMs)
}

/** ZPL straight to a Zebra on the warehouse network. */
export function requestPrintRaw(job: { host: string, port?: number, data: string }, limitMs = RAW_LIMIT_MS): Promise<PrintAnswer> {
  return request({ command: PRINT_COMMANDS.raw, host: job.host.trim(), port: String(job.port ?? 9100), data: job.data, encoding: 'utf-8' }, limitMs)
}

/** The app answered: hand it to the screen that asked. An answer nobody is waiting for is dropped. */
export function settlePrint(answer: { requestId: string } & PrintAnswer): void {
  waiting.get(answer.requestId)?.({ status: answer.status, errorCode: answer.errorCode, errorMessage: answer.errorMessage })
}

/** What to tell the driver when it did not print; null when it did. */
export function printProblem(answer: PrintAnswer): string | null {
  if (answer.status === 'ok') return null
  if (answer.status === 'cancelled') return 'Printing was cancelled.'
  return answer.errorMessage.trim() || 'The tablet could not print.'
}
